import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { Drawer, TextField, Button, Box, IconButton, Grid, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InputField from "../../components/InputField";
import { t } from "i18next";
import styles from "./styles";
import CustomSelect from "../../components/SelectField";

import { PlanType } from "./types";
import { Status } from "./types";
import { useTranslation } from "react-i18next";

type FilterInputs = {
    planType: string;
    code: string;
    name: string;
    streetName: string;
    streetNumber: string;
    city: string;
    postalCode: string;
    status: string;
};

type FilterDrawerProps = {
    drawerOpen: boolean;
    toggleDrawer: (open: boolean) => () => void;
    handleApplyFilters: (filters: FilterInputs) => void;
};

const defaultValues = {
    planType: "",
    code: "",
    name: "",
    streetName: "",
    streetNumber: "",
    city: "",
    postalCode: "",
    status: ""
};


const planTypeList = Object.entries(PlanType).map((rec) => ({
    name: rec[0],
    value: rec[1],
}));


const statusList = Object.entries(Status).map((rec) => ({
    name: rec[0],
    value: rec[1],
}));



const FilterDrawer = ({ drawerOpen, toggleDrawer, handleApplyFilters }: FilterDrawerProps) => {
     const { t } = useTranslation();
    const { formState: { errors }, control, register, handleSubmit, reset } = useForm<FilterInputs>({ defaultValues: defaultValues });

    const onSubmit: SubmitHandler<FilterInputs> = (data) => {
        handleApplyFilters(data);
        toggleDrawer(false)();
    };

    const handleClear = () => {
        reset(); // Clear the form fields
        handleApplyFilters({ planType: "", code: "", name: "", streetName: "", streetNumber: "", city: "", postalCode: "", status: "" }); // Clear filters
        // toggleDrawer(false)();
    };



    return (
        <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)} sx={styles.filterDrawer}  >

            <Box component="form" sx={{ ...styles.formContainer, paddingLeft: "1rem", paddingRight: "1rem" }} onSubmit={handleSubmit(onSubmit)}>
                <Typography sx={styles.subHeading}>{t("locations")}</Typography>
                <IconButton
                    onClick={toggleDrawer(false)}
                    sx={{ position: "absolute", top: 8, right: 8 }}
                >
                    <CloseIcon />
                </IconButton>
                <Grid container spacing={2} xs={14} >

                    <Grid item xs={6}>
                        <Controller
                            name="planType"
                            control={control}
                            render={({ field }) => (
                                <CustomSelect
                                    {...field}
                                    id="planType"
                                    onChange={(e) => field.onChange(e.target.value)}
                                    size="small"
                                    fullWidth
                                    placeholder={t("planType")}
                                    items={planTypeList}
                                    error={!!errors.status?.message}
                                    errorMessage={errors.status?.message}
                                    label={t("planType")}
                                />

                            )}
                        />

                    </Grid>
                         <Grid item xs={6}>
                        <Controller
                            name="code"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id="code"
                                    label={t("locationCode")}
                                    placeholder={t("locationCode")}
                                    type="text"
                                    showCross
                                    fullWidth
                                    size="small"
                                    handleCrossClick={() => field.onChange("")}
                                    error={!!errors.code?.message}
                                    errorMessage={errors.code?.message}
                                />
                            )}
                        />

                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id="name"
                                    label={t("name")}
                                    placeholder={t("name")}
                                    type="text"
                                    showCross
                                    fullWidth
                                    size="small"
                                    handleCrossClick={() => field.onChange("")}
                                    error={!!errors.name?.message}
                                    errorMessage={errors.name?.message}
                                />
                            )}
                        />

                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="streetName"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id="streetName"
                                    label={t("streetName")}
                                    placeholder={t("streetName")}
                                    type="text"
                                    showCross
                                    fullWidth
                                    size="small"
                                    handleCrossClick={() => field.onChange("")}
                                    error={!!errors.streetName?.message}
                                    errorMessage={errors.streetName?.message}
                                />
                            )}
                        />

                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="streetNumber"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id="streetNumber"
                                    label={t("streetNumber")}
                                    placeholder={t("streetNumber")}
                                    type="text"
                                    showCross
                                    fullWidth
                                    size="small"
                                    handleCrossClick={() => field.onChange("")}
                                    error={!!errors.streetNumber?.message}
                                    errorMessage={errors.streetNumber?.message}
                                />
                            )}
                        />

                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="city"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id="city"
                                    label={t("city")}
                                    placeholder={t("city")}
                                    type="text"
                                    showCross
                                    fullWidth
                                    size="small"
                                    handleCrossClick={() => field.onChange("")}
                                    error={!!errors.city?.message}
                                    errorMessage={errors.city?.message}
                                />
                            )}
                        />

                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            name="postalCode"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id="postalCode"
                                    label={t("postalCode")}
                                    placeholder={t("postalCode")}
                                    type="text"
                                    showCross
                                    fullWidth
                                    size="small"
                                    handleCrossClick={() => field.onChange("")}
                                    error={!!errors.postalCode?.message}
                                    errorMessage={errors.postalCode?.message}
                                />
                            )}
                        />

                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <CustomSelect
                                    {...field}
                                    id="status"
                                    onChange={(e) => field.onChange(e.target.value)}
                                    size="small"
                                    fullWidth
                                    placeholder="Status"
                                    items={statusList}
                                    error={!!errors.status?.message}
                                    errorMessage={errors.status?.message}
                                    label="Status"
                                />

                            )}
                        />

                    </Grid>
                </Grid>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "8px",
                        marginTop: "16px",
                        paddingBottom: "3rem",
                    }}
                >
                    <Button
                        variant="outlined"
                        color="secondary"
                        fullWidth
                        onClick={handleClear}
                    >
                           {t("clear")}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                    >
                           {t("filter")}
                    </Button>
                </Box>

            </Box>

        </Drawer>
    );
};

export default FilterDrawer;
